// StoryReader: title and teaching point, the text with tappable words and translations, then (a) comprehension
// questions, (b) the sentence-ordering exercise and (c) the new-word list (beside the text on wide screens).

import type { FC, ReactNode } from 'react'
import type { ReaderStory, StoryTitle } from '@/prakrit/components/stories/story-logic'

import Link from 'next/link'
import Image from 'next/image'
import { useCallback, useState } from 'react'
import {
    ArrowLeft,
    ArrowRight,
    BookOpenText,
    Eye,
    Hourglass,
    Lightbulb,
    MessagesSquare,
    PartyPopper
} from 'lucide-react'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { Bi } from '@/prakrit/components/common/bi'
import { ReviewerOnly, StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { CONTAINER, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { LevelLabel, LevelSuggestion } from '@/prakrit/components/stories/story-card'
import { StoryText } from '@/prakrit/components/stories/story-text'
import { StoryQuestions } from '@/prakrit/components/stories/story-questions'
import { StoryOrder } from '@/prakrit/components/stories/story-order'
import { StoryWords } from '@/prakrit/components/stories/story-words'
import { storyImageSrc } from '@/prakrit/components/stories/story-logic'

export interface NextStory {
    id: string
    title: StoryTitle
}

const backOf = (kind: 'story' | 'dialogue') => (kind === 'dialogue'
    ? { href: '/stories/baatcheet', hi: 'सभी बातचीत', en: 'All dialogues' }
    : { href: '/stories', hi: 'सभी कथाएँ', en: 'All stories' })

const BackLink: FC<{ kind: 'story' | 'dialogue' }> = ({ kind }): ReactNode => {
    const back = backOf(kind)

    return (
        <nav aria-label='Breadcrumb'>
            <Link
                href={back.href}
                className='group inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-card/80 px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:text-prakrit'
            >
                <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                <span lang='hi' className='hindi'>{back.hi}</span>
                <span aria-hidden='true' className='text-muted-foreground'>·</span>
                <span lang='en'>{back.en}</span>
            </Link>
        </nav>
    )
}

const ReaderHeader: FC<{ story: ReaderStory, preview: boolean }> = ({ story, preview }): ReactNode => {
    const KindIcon = story.kind === 'dialogue' ? MessagesSquare : BookOpenText

    return (
        <header className='flex flex-col gap-6 pb-8 pt-6 sm:pb-10 sm:pt-10'>
            <BackLink kind={story.kind} />

            {preview && (
                <ReviewerOnly>
                    <p className='flex items-start gap-2 rounded-xl border border-saffron/40 bg-saffron/10 px-4 py-3 text-sm'>
                        <Eye className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                        <Bi
                            hi='समीक्षा पूर्वावलोकन: शिक्षार्थी यह कथा अभी नहीं देखते। यहाँ उत्तर जाँचे जाते हैं, पर अंक या प्रगति नहीं जुड़ती।'
                            en='Reviewer preview: learners cannot see this story yet. Answers are checked here, but no XP or progress is recorded.'
                        />
                    </p>
                </ReviewerOnly>
            )}

            <div className='grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]'>
                <div className='flex max-w-3xl flex-col gap-3'>
                    <p className='flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-saffron-strong'>
                        <span className='inline-flex items-center gap-1.5'>
                            <KindIcon className='size-4' aria-hidden='true' />
                            <span lang='hi' className='hindi'>{story.kind === 'dialogue' ? 'बातचीत' : 'कथा'}</span>
                            <span lang='en' className='font-medium text-muted-foreground'>{story.kind === 'dialogue' ? 'Dialogue' : 'Story'}</span>
                        </span>
                        <LevelLabel level={story.level} className='font-medium text-foreground' />
                        {story.status !== 'published' && <StatusBadge kind={reviewBadgeKind(story.status)} />}
                    </p>
                    <h1 className='flex flex-col gap-2'>
                        <span className='pra pra-display'>
                            <PrakritText text={story.title.pra} />
                        </span>
                        <span className='flex flex-wrap items-baseline gap-x-3'>
                            <span lang='hi' className='hindi text-xl font-semibold text-foreground sm:text-2xl'>{story.title.hi}</span>
                            <span lang='en' className='font-sans text-lg font-medium text-muted-foreground sm:text-xl'>{story.title.en}</span>
                        </span>
                    </h1>
                    <p lang='pra-Latn' className='translit text-sm italic text-muted-foreground'>{transliterate(story.title.pra)}</p>
                    <LevelSuggestion level={story.level} className='max-w-xl' />
                    {story.teachingPoint && (
                        <div className='flex max-w-2xl items-start gap-3 rounded-xl border border-terracotta/30 bg-terracotta/6 px-4 py-3 text-sm'>
                            <Lightbulb className='mt-0.5 size-5 shrink-0 text-terracotta' aria-hidden='true' />
                            <div className='flex flex-col gap-1'>
                                <p className='font-semibold'>
                                    <Bi hi='ध्यान दें' en='Notice' layout='inline' enClassName='font-medium text-muted-foreground' />
                                </p>
                                <Bi hi={story.teachingPoint.hi} en={story.teachingPoint.en} className='gap-0.5 leading-relaxed' />
                            </div>
                        </div>
                    )}
                </div>

                {story.image && (
                    <figure className='overflow-hidden rounded-2xl border border-border bg-card shadow-card'>
                        <Image
                            src={storyImageSrc(story.image)}
                            alt={`${story.title.hi} · ${story.title.en}`}
                            width={1200}
                            height={800}
                            sizes='(min-width: 1024px) 600px, 100vw'
                            priority
                            className='aspect-video h-auto w-full object-cover'
                        />
                    </figure>
                )}
            </div>
        </header>
    )
}

const DoneCard: FC<{ xp: number, preview: boolean, next: NextStory | null, kind: 'story' | 'dialogue' }> = ({ xp, preview, next, kind }): ReactNode => {
    const back = backOf(kind)

    return (
        <section aria-labelledby='story-done' className='flex flex-col items-center gap-4 rounded-2xl border border-success/40 bg-success-soft px-5 py-8 text-center animate-in fade-in zoom-in-95 duration-300'>
            <span aria-hidden='true' className='flex size-14 items-center justify-center rounded-full bg-card text-success shadow-card'>
                <PartyPopper className='size-7' />
            </span>
            <h2 id='story-done' className='flex flex-col gap-0.5'>
                <span lang='hi' className='hindi text-2xl font-bold text-foreground'>{kind === 'dialogue' ? 'बातचीत पूरी!' : 'कथा पूरी!'}</span>
                <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>
                    {kind === 'dialogue' ? 'You have finished this dialogue.' : 'You have finished this story.'}
                </span>
            </h2>
            {!preview && xp > 0 && (
                <p className='text-sm'>
                    <span lang='hi' className='hindi adults-only'>इस कथा से आज {hiDigits(xp)} अंक मिले।</span>
                    <span lang='hi' className='hindi kids-only'>तुम्हें {hiDigits(xp)} अंक मिले!</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en' className='text-muted-foreground'>{xp} XP earned today.</span>
                </p>
            )}
            <div className='flex flex-col gap-2.5 sm:flex-row'>
                {next && (
                    <Link
                        href={`/stories/${encodeURIComponent(next.id)}`}
                        className='inline-flex min-h-tap items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/85'
                    >
                        <span lang='hi' className='hindi'>अगली:</span>
                        <span lang='pra' className='font-prakrit font-semibold'>{next.title.pra}</span>
                        <span lang='en' className='font-medium'>{next.title.en}</span>
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                )}
                <Link
                    href={back.href}
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <span lang='hi' className='hindi'>{back.hi}</span>
                    <span aria-hidden='true' className='text-muted-foreground'>·</span>
                    <span lang='en'>{back.en}</span>
                </Link>
            </div>
        </section>
    )
}

interface StoryReaderProps {
    story: ReaderStory
    next: NextStory | null
    preview: boolean
    /** A quiet row under the text and exercises (the reviewer credit and "Report a language issue"). */
    footer?: ReactNode
}

export const StoryReader: FC<StoryReaderProps> = ({ story, next, preview, footer }): ReactNode => {
    const [solved, setSolved] = useState<Record<string, number>>({})
    const total = story.questions.length + (story.orderTiles.length > 1 ? 1 : 0)
    const done = total > 0 && Object.keys(solved).length >= total
    const xp = Object.values(solved).reduce((sum, value) => sum + value, 0)

    const onQuestion = useCallback((index: number, earned: number) => {
        setSolved(previous => ({ ...previous, [`q${index}`]: earned }))
    }, [])

    const onOrder = useCallback((earned: number) => {
        setSolved(previous => ({ ...previous, order: earned }))
    }, [])

    return (
        <div className={CONTAINER}>
            <ReaderHeader story={story} preview={preview} />

            <div className='grid gap-10 pb-16 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start xl:gap-12'>
                <div className='flex min-w-0 flex-col gap-12'>
                    <StoryText story={story} />
                    <StoryQuestions story={story} onSolved={onQuestion} />
                    <StoryOrder story={story} onSolved={onOrder} />
                    {done && <DoneCard xp={xp} preview={preview} next={next} kind={story.kind} />}
                    {footer && <div className='border-t border-border pt-3'>{footer}</div>}
                </div>

                <aside aria-label='शब्द · Words' className='lg:sticky lg:top-24 lg:max-h-[calc(100vh_-_7rem)] lg:overflow-y-auto lg:pb-2'>
                    <StoryWords words={story.words} preTaught={story.preTaught} />
                </aside>
            </div>
        </div>
    )
}

/** Shown instead of the reader while a story awaits a teacher's review (learners only, so no review wording). */
export const StoryAwaiting: FC<{ title: StoryTitle, kind: 'story' | 'dialogue', level: number }> = ({ title, kind, level }): ReactNode => {
    const back = backOf(kind)

    return (
        <section className='mx-auto flex w-full max-w-xl flex-col items-center gap-5 px-4 py-14 text-center sm:py-20'>
            <span className='flex size-16 items-center justify-center rounded-full bg-secondary ring-1 ring-border' aria-hidden='true'>
                <Hourglass className='size-8 text-saffron-strong' />
            </span>
            <p className='flex flex-col items-center gap-1'>
                <span lang='pra' className='pra pra-xl'>{title.pra}</span>
                <span className='text-sm text-muted-foreground'>
                    <Bi hi={title.hi} en={title.en} layout='inline' className='justify-center' always />
                </span>
                <LevelLabel level={level} className='text-sm' />
            </p>
            <h1 className='font-heading text-2xl font-bold leading-snug sm:text-3xl'>
                <span lang='hi' className='block text-prakrit'>{kind === 'dialogue' ? 'यह बातचीत' : 'यह कथा'} जल्द खुलेगी</span>
                <span lang='en' className='mt-1 block font-sans text-lg font-medium text-muted-foreground'>
                    {kind === 'dialogue' ? 'This dialogue' : 'This story'} opens soon
                </span>
            </h1>
            <p className='text-base'>
                <Bi
                    hi={`${kind === 'dialogue' ? 'यह बातचीत' : 'यह कथा'} अभी तैयार की जा रही है। तब तक दूसरी कथाएँ पढ़िए या पाठ कीजिए।`}
                    en={`${kind === 'dialogue' ? 'This dialogue' : 'This story'} is still being prepared. Meanwhile, read another story or take a lesson.`}
                    enClassName='mt-1'
                />
            </p>
            <div className='flex flex-col gap-2.5 sm:flex-row'>
                <Link
                    href={back.href}
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 font-medium shadow-sm hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <ArrowLeft className='size-4 text-saffron-strong' aria-hidden='true' />
                    <Bi hi={back.hi} en={back.en} layout='inline' always enClassName='text-muted-foreground' />
                </Link>
                <Link
                    href='/learn'
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 font-medium shadow-sm hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <BookOpenText className='size-4 text-saffron-strong' aria-hidden='true' />
                    <Bi hi='पाठ्यक्रम' en='Course map' layout='inline' always enClassName='text-muted-foreground' />
                </Link>
            </div>
        </section>
    )
}
